import { defaultLocale } from '@desktopClient/helpers/defaultLocale';

const config = {
  warcraftLogsBaseUrl: process.env.WARCRAFT_LOGS_BASE_URL ?? '/',
  warcraftLogsClassicBaseUrl: process.env.WARCRAFT_LOGS_CLASSIC_BASE_URL ?? '/',
  warcraftLogsSeasonOfDiscoveryBaseUrl:
    process.env.WARCRAFT_LOGS_SEASON_OF_DISCOVERY_BASE_URL ?? '/',
  warcraftLogsClassic2BaseUrl:
    process.env.WARCRAFT_LOGS_CLASSIC_2_BASE_URL ?? '/',
  warcraftLogsClassicVanillaBaseUrl:
    process.env.WARCRAFT_LOGS_CLASSIC_VANILLA_BASE_URL ?? '/',
  warcraftLogsClassicForeverBaseUrl:
    process.env.WARCRAFT_LOGS_CLASSIC_FOREVER_BASE_URL ?? '/',
  ffLogsBaseUrl: process.env.FF_LOGS_BASE_URL ?? '/',
  esoLogsBaseUrl: process.env.ESO_LOGS_BASE_URL ?? '/',
  swtorLogsBaseUrl: process.env.SWTOR_LOGS_BASE_URL ?? '/',
  fellowshipLogsBaseUrl: process.env.FELLOWSHIP_LOGS_BASE_URL ?? '/',
  siteName: process.env.SITE_NAME,
  isBeta: process.env.IS_BETA === 'true',
  isProduction: process.env.IS_PRODUCTION === 'true',
  isCI: process.env.CI === 'true',
  isDevelopment: process.env.IS_DEVELOPMENT === 'true',
  isStaging: process.env.IS_STAGING === 'true',
} as Config;

type Config = {
  warcraftLogsBaseUrl: string;
  warcraftLogsClassicBaseUrl: string;
  warcraftLogsSeasonOfDiscoveryBaseUrl: string;
  warcraftLogsClassic2BaseUrl: string;
  warcraftLogsClassicVanillaBaseUrl: string;
  warcraftLogsClassicForeverBaseUrl: string;
  ffLogsBaseUrl: string;
  esoLogsBaseUrl: string;
  swtorLogsBaseUrl: string;
  fellowshipLogsBaseUrl: string;
  siteName: 'archon';
  isBeta: boolean;
  isProduction: boolean;
  isCI: boolean;
  isDevelopment: boolean;
  isStaging: boolean;
};

export const gameSlugs = [
  'wow',
  'classic-mop',
  'classic-cn',
  'classic-sod',
  'classic-fresh',
  'classic-titan-reforged',
  'classic-vanilla',
  'wow-forever',
  'ffxiv',
  'eso',
  'swtor',
  'fellowship',
] as const;
export type GameSlug = (typeof gameSlugs)[number];
export const toGameSlug = (gameSlug: string): GameSlug | null => {
  if (gameSlugs.includes(gameSlug as GameSlug)) {
    return gameSlug as GameSlug;
  }

  return null;
};

export function getApiClientId(gameSlug?: GameSlug): string {
  switch (gameSlug) {
    case 'wow':
    case 'classic-mop':
    case 'classic-cn':
    case 'classic-sod':
    case 'classic-fresh':
    case 'classic-titan-reforged':
    case 'classic-vanilla':
    case 'wow-forever':
      return process.env.WARCRAFT_LOGS_API_CLIENT_ID as string;
    case 'ffxiv':
      return process.env.FF_LOGS_API_CLIENT_ID as string;
    case 'eso':
      return process.env.ESO_LOGS_API_CLIENT_ID as string;
    case 'swtor':
      return process.env.SWTOR_LOGS_API_CLIENT_ID as string;
    case 'fellowship':
      return process.env.FELLOWSHIP_LOGS_API_CLIENT_ID as string;
  }

  return process.env.RPGLOGS_API_CLIENT_ID as string;
}

export function getApiClientSecret(gameSlug?: GameSlug): string {
  switch (gameSlug) {
    case 'wow':
    case 'classic-mop':
    case 'classic-cn':
    case 'classic-sod':
    case 'classic-fresh':
    case 'classic-titan-reforged':
    case 'classic-vanilla':
    case 'wow-forever':
      return process.env.WARCRAFT_LOGS_API_CLIENT_SECRET as string;
    case 'ffxiv':
      return process.env.FF_LOGS_API_CLIENT_SECRET as string;
    case 'eso':
      return process.env.ESO_LOGS_API_CLIENT_SECRET as string;
    case 'swtor':
      return process.env.SWTOR_LOGS_API_CLIENT_SECRET as string;
    case 'fellowship':
      return process.env.FELLOWSHIP_LOGS_API_CLIENT_SECRET as string;
  }

  return process.env.RPGLOGS_API_CLIENT_SECRET as string;
}

export function getBaseUrl(
  gameSlug?: GameSlug,
  locale?: string | null,
): string {
  const baseUrl = (() => {
    switch (gameSlug) {
      case 'wow':
        return config.warcraftLogsBaseUrl;
      case 'classic-mop':
        return config.warcraftLogsClassicBaseUrl;
      case 'classic-cn':
        return config.warcraftLogsClassicBaseUrl
          .split('classic.')
          .join('cn.classic.');
      case 'classic-sod':
        return config.warcraftLogsSeasonOfDiscoveryBaseUrl;
      case 'classic-fresh':
        return config.warcraftLogsClassic2BaseUrl;
      case 'classic-titan-reforged':
        return config.warcraftLogsClassicBaseUrl
          .split('classic.')
          .join('cn.titan.');
      case 'classic-vanilla':
        return config.warcraftLogsClassicVanillaBaseUrl;
      case 'wow-forever':
        return config.warcraftLogsClassicForeverBaseUrl;
      case 'ffxiv':
        return config.ffLogsBaseUrl;
      case 'eso':
        return config.esoLogsBaseUrl;
      case 'swtor':
        return config.swtorLogsBaseUrl;
      case 'fellowship':
        return config.fellowshipLogsBaseUrl;
    }

    return process.env.RPGLOGS_BASE_URL as string;
  })();

  if (!locale) {
    return baseUrl;
  }

  return applyLocaleToBaseUrl(baseUrl, locale, config.isStaging);
}

export function applyLocaleToBaseUrl(
  baseUrl: string,
  locale: string,
  isStaging: boolean,
): string {
  try {
    const url = new URL(baseUrl);
    const parts = url.hostname.split('.');

    const localeIndex = isStaging ? 1 : 0;

    if (isLocalePrefix(parts[localeIndex])) {
      if (locale === defaultLocale) {
        parts.splice(localeIndex, 1);
      } else {
        parts[localeIndex] = locale;
      }
    } else if (locale !== defaultLocale) {
      parts.splice(localeIndex, parts[localeIndex] === 'www' ? 1 : 0, locale);
    }

    return `${url.protocol}//${parts.join('.')}`;
  } catch {
    return baseUrl;
  }
}

function isLocalePrefix(hostPart: string): boolean {
  return /^[a-z]{2}$/i.test(hostPart) && hostPart !== 'www';
}

export default config;
